<script lang="ts">
	import Layout from "../components/Layout.svelte";
	import { getTenant } from "../getTenant.js";
	import type { SolutionsSubcategoryProps } from "./SolutionsSubcategoryProps.js";
	import racksHero from "../assets/images/racks/racks-hero.jpg";
	import racksHeroSm from "../assets/images/racks/racks-hero.jpg?webp&h=405&w=608&imagetools";
	import racksHeroMd from "../assets/images/racks/racks-hero.jpg?webp&h=470&w=704&imagetools";
	import racksHeroLg from "../assets/images/racks/racks-hero.jpg?webp&h=640&w=960&imagetools";
	import racksHeroXl from "../assets/images/racks/racks-hero.jpg?webp&h=420&w=1424&imagetools";
	import cranesHero from "../assets/images/solutions/cranes.png";
	import cranesHeroSm from "../assets/images/solutions/cranes.png?webp&h=405&w=608&imagetools";
	import cranesHeroMd from "../assets/images/solutions/cranes.png?webp&h=470&w=704&imagetools";
	import cranesHeroLg from "../assets/images/solutions/cranes.png?webp&h=640&w=960&imagetools";
	import cranesHeroXl from "../assets/images/solutions/cranes.png?webp&h=420&w=1424&imagetools";
	import robotsHero from "../assets/images/solutions/robots.png";
	import robotsHeroSm from "../assets/images/solutions/robots.png?webp&h=405&w=608&imagetools";
	import robotsHeroMd from "../assets/images/solutions/robots.png?webp&h=470&w=704&imagetools";
	import robotsHeroLg from "../assets/images/solutions/robots.png?webp&h=640&w=960&imagetools";
	import robotsHeroXl from "../assets/images/solutions/robots.png?webp&h=420&w=1424&imagetools";
	import StaticPicture from "../components/Picture/StaticPicture.svelte";
	import { Category, categoryNameMap } from "../../../core/schema/Category.js";
	import { nbspify } from "../../../core/utils/nbspify.js";
	import Card from "../components/Card.svelte";
	import { getTranslate } from "../utils/getTranslate.js";
	import translations from "./SolutionsSubcategory.translations.json";
	import ReferencesSlider from "../components/Sliders/ReferencesSlider.svelte";
	import { getProductDetailPath } from "../../../core/schema/paths/getProductDetailPath.js";
	import Breadcrumbs from "../components/Breadcrumbs.svelte";
	import type { SourceSetQuery } from "../components/Picture/SourceSetQuery.js";
	import { getContext, setContext } from "svelte";
	import { thumbnailImageContextKey } from "../contexts/thumbnailImageContextKey.js";
	import type { GlobalTemplateProps } from "../GlobalTemplateProps.js";
	import { pageSlugContextKey } from "../contexts/pageSlugContextKey.js";

	type $$Props = SolutionsSubcategoryProps;
	const translate = getTranslate(translations);

	const tenant = getTenant();
	const pageSlug = getContext<GlobalTemplateProps["pageSlug"]>(pageSlugContextKey);
	const { products, category, texts, references, breadcrumbs } = $$restProps as $$Props;

	const categoryImageMap = {
		[Category.Cranes]: {
			thumbnail: cranesHero,
			"<md": cranesHeroSm,
			">md": cranesHeroMd,
			">lg": cranesHeroLg,
			">xl": cranesHeroXl,
		},
		[Category.Robots]: {
			thumbnail: robotsHero,
			"<md": robotsHeroSm,
			">md": robotsHeroMd,
			">lg": robotsHeroLg,
			">xl": robotsHeroXl,
		},
		[Category.Racks]: {
			thumbnail: racksHero,
			"<md": racksHeroSm,
			">md": racksHeroMd,
			">lg": racksHeroLg,
			">xl": racksHeroXl,
		},
	} satisfies Record<
		Category,
		Partial<Record<SourceSetQuery, string>> & {
			thumbnail: string;
		}
	>;

	setContext(thumbnailImageContextKey, categoryImageMap[category].thumbnail);
</script>

<Layout>
	<div class="container">
		<div class="flex items-center overflow-hidden rounded-lg">
			<StaticPicture
				image={categoryImageMap[category][">xl"]}
				alt={categoryNameMap[category][tenant.locale]}
				width={1424}
				height={420}
				loading="eager"
				class="relative size-full items-center justify-center overflow-hidden xl:flex xl:h-[26.25rem]"
				imgClass="rounded-lg size-full object-cover inset-0 max-w-[initial] xl:absolute"
				sourcesets={categoryImageMap[category]}
			/>
		</div>
	</div>
	<Breadcrumbs pageSlug={pageSlug[tenant.locale] ?? ""} {breadcrumbs} />
	<section class="container flex justify-center">
		<div class="lg:w-[50rem]">
			<h1 class="m-0 mb-4 flex flex-col p-0 text-lg/[1.1] font-normal md:text-xl lg:mb-10 lg:w-3/4">
				<span class="font-bold">{nbspify(texts.headline[tenant.locale])}</span>
				{nbspify(texts.subheadline[tenant.locale])}
			</h1>
			<p class="mb-16 flex flex-col gap-4 text-xs/[1.4rem]">
				{#each texts.texts[tenant.locale] as paragraph}
					<span>{nbspify(paragraph)}</span>
				{/each}
			</p>
			<!-- Out of MVP - no data for these boxes... -->
			<!-- <div class="grid grid-cols-1 gap-2 md:grid-cols-2">
				{#each texts.cards as { icon, title, points }}
					<SubcategorySolutionCard icon={cardsIconMap[icon]} title={title[tenant.locale]} {points} />
				{/each}
			</div> -->
		</div>
	</section>
	{#if products.length > 0}
		<section class="container">
			<h2 class="mb-10 text-center text-lg md:text-3xl">{translate("products")}</h2>
			<div class="grid grid-cols-1 gap-2 md:grid-cols-2 lg:grid-cols-4">
				{#each products as product}
					<Card
						image={product.primaryImage}
						title={product.name}
						description={product.perex}
						urlSlug={getProductDetailPath(category, product)}
						squaredImage
						containImage
					/>
				{/each}
			</div>
		</section>
	{/if}
	{#if references.length > 0}
		<section class="container">
			<ReferencesSlider {references} />
		</section>
	{/if}
</Layout>
